import { ActionButtonData, MessageDto, MessageSuggestionsDto, MessageType } from 'client/types';
import { Board, InternalChatState, Message, MessageFrom, MessageMethod } from 'types';

/**
 * Obtiene a partir de un DTO de un mensaje, el mensaje que corresponde
 * @param dto
 */
export function transformMessage(dto: MessageDto | Message): Message {
  if (typeof dto === 'object' && 'from' in dto && 'isUser' in dto) {
    return dto;
  }

  // Transform object only if it is a MessageDto
  const { id, text, type, timestamp, context, suggestions, redirectTo, content } = dto as MessageDto;

  return {
    id,
    text,
    suggestions,
    board: transformBoard(dto as MessageDto),
    from: type === 'USER' ? 'USER' : 'BACKEND',
    isUser: type.toUpperCase() === 'USER',
    isAssistant: type.toUpperCase() === 'ASSISTANT',
    isSystem: false,
    hasBoard: typeof content !== 'undefined' && (content?.components?.length ?? 0) > 0,
    method: 'TEXT',
    variant: dto.variant,
    feedback: null,
    timestamp,
    context,
    type: type.toUpperCase() as MessageType,
    redirectTo,
  };
}

/**
 * Construye un mensaje a partir de un texto y un tipo
 * @param text
 * @param type
 * @param timestamp
 * @param from
 * @param method
 * @param suggestions
 */
export function buildMessage(
  text: string,
  type: MessageType,
  timestamp: number,
  from: MessageFrom,
  method: MessageMethod,
  suggestions?: MessageSuggestionsDto[],
  action?: ActionButtonData,
): Message {
  return {
    text,
    type,
    timestamp,
    from,
    method,
    suggestions,
    action,
    isSystem: from === 'SYSTEM',
    isUser: type === 'USER',
    isAssistant: type === 'ASSISTANT',
    feedback: null,
    hasBoard: false,
    id: `user-${timestamp}`,
  };
}

/**
 * Obtiene a partir de un DTO de un mensaje el Board asociado en el caso de existir.
 * @param dto
 */
export function transformBoard(dto: MessageDto): Board | undefined {
  if (typeof dto.content === 'undefined' || dto.content.components?.length === 0) return undefined;

  const { id, context, content, timestamp } = dto;

  return {
    id,
    timestamp,
    context,
    content,
  };
}

/**
 * Ordena los mensajes de menos reciente a mas reciente
 * @param a
 * @param b
 */
export function sortMessage(a: Message, b: Message) {
  return a.timestamp - b.timestamp;
}

/**
 * Determina si el message id iterado, es el mas reciente
 * @param messages
 * @param messageId
 */
export function isLatestMessage(messages: Message[], messageId: string) {
  return messages.length > 0 && messages[messages.length - 1].id === messageId;
}

/**
 * Calcula la fecha mas reciente de un arreglo de mensajes
 * @param messages
 */
export function calculateLatestDate(messages: Message[]) {
  return Math.max(...messages.map(({ timestamp }) => timestamp)) + 10;
}

/**
 * Transforma un mensaje o arreglo de mensajes en mensajes y boards
 * @param state
 * @param newMessage
 * @param resetMessages
 */
export function transformMessages(
  state: InternalChatState,
  newMessage: MessageDto | MessageDto[] | Message | undefined,
  resetMessages: boolean = false,
): {
  messages: Message[];
} {
  const messagesDto = Array.isArray(newMessage) ? newMessage : typeof newMessage !== 'undefined' ? [newMessage] : [],
    messagesTransformed = messagesDto.map(dto => transformMessage(dto)),
    messages = resetMessages ? [...messagesTransformed] : [...state.messages, ...messagesTransformed];

  return {
    messages: messages.sort(sortMessage),
  };
}

/**
 * Verifica si en la lista de mensajes, hay un mensaje que fue enviado por el usuario
 * @param messages
 */
export function hasMessageSentFromUser(messages: Message[]): boolean {
  return messages.some(({ type, from }) => type === 'USER' && from !== 'BACKEND');
}

/**
 * Get a previous message by user with a specific ID
 * @param messages
 * @param messageId
 */
export function getPrevMessageByUserWithId(messages: Message[], messageId: string): Message | undefined {
  const indexMessage = messages.findIndex(({ id }) => id === messageId);
  return messages[indexMessage - 1];
}

/**
 * Save chatId and userId in session storage for auto-restore chat
 * @param chatId
 * @param userId
 */
export function saveChatAndUserId(chatId: string, userId: string): void {
  console.debug('Chat ID and User ID saved in sessionStorage:', chatId, userId);
  sessionStorage.setItem('chatId', chatId);
  sessionStorage.setItem('userId', userId);
}

/**
 * Save a message to restore after page reload
 * @param messageText
 */
export function saveMessageToRestore(messageText: string | undefined): void {
  if (typeof messageText === 'string') {
    console.debug('Save message to restore after page reload:', messageText);
    sessionStorage.setItem('messageToRestore', messageText);
  }
}

/**
 * Evaluate has a message to restore after page reload
 * @returns boolean
 */
export function messageToRestore(): { has: boolean; value: string | null } {
  const messageToRestore = sessionStorage.getItem('messageToRestore');

  return {
    has: messageToRestore !== null,
    value: messageToRestore,
  };
}

/**
 * Check if chatId and userId are stored in session storage
 * @returns boolean
 */
export function chatAndUserId(): { hasAll: boolean; hasChatId: boolean; hasUserId: boolean; chatId: string | null; userId: string | null } {
  const chatId = sessionStorage.getItem('chatId');
  const userId = sessionStorage.getItem('userId');

  return {
    hasAll: chatId !== null && userId !== null,
    hasChatId: chatId !== null,
    hasUserId: userId !== null,
    chatId: chatId,
    userId: userId,
  };
}
